import { validateEmail } from "@op/opds.validate";
import { reaction } from "mobx";
import { useEffect } from "react";
import {
  browserName,
  browserVersion,
  osName,
  osVersion,
} from "react-device-detect";
import {
  BANK_FEEDBACK,
  BANKING,
  EMPTY_ERROR_CODE,
  INITIAL_VALUE,
  INSURANCE,
  INSURANCE_FEEDBACK,
  INVALID_EMAIL_CHARS,
  INVALID_TEXT_AREA_ERROR_CODE,
  INVALID_TEXT_FIELD_ERROR_CODE,
  OTHER,
} from "./enums";

export const displayAndFocusAtOpFiTemplateTitle = (action) => {
  const headings = document.getElementsByClassName("op-layout-heading");
  const mainHeading = headings.length > 0 ? headings[0] : null;
  if (mainHeading && mainHeading.style) {
    mainHeading.style.display = "block";

    // If user navigates back to the page, move focus to main heading
    if (action === "PUSH") {
      mainHeading.tabIndex = -1;
      mainHeading.focus();
    }
  }
};

export const hideOpFiTemplateTitle = () => {
  const headings = document.getElementsByClassName("op-layout-heading");
  const mainHeading = headings.length > 0 ? headings[0] : null;
  if (mainHeading && mainHeading.style) mainHeading.style.display = "none";
};

export const scrollToTop = () => {
  window.scrollTo(0, 0);
};

export const focusToWindow = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
  window.focus();
};

export const setInitialValueAndRequired = (value, required) => {
  return { ...INITIAL_VALUE, value, required };
};

export const isBlank = (str) => {
  return !str || /^\s*$/.test(str);
};

const createError = (content, code) => {
  const error = new Error(content);
  error.code = code;
  return error;
};

export const emptyError = (msgs) => {
  return createError(msgs("error.empty"), EMPTY_ERROR_CODE);
};

export const invalidTextFieldError = (msgs) => {
  return createError(msgs("error.invalid"), INVALID_TEXT_FIELD_ERROR_CODE);
};

export const invalidTextAreaError = () => {
  return createError("", INVALID_TEXT_AREA_ERROR_CODE);
};

export const resolveMaxRows = () => {
  if (window.innerWidth <= 799) {
    return 5;
  }
  return 10;
};

export const isValidText = (value) => {
  if (isBlank(value)) return true;

  const allowedChars = /^[a-zA-ZÀ-ÖØ-öø-ÿ0-9ÄäÖöÅå.,?!()+-/&:"'‘’“” \r\n]+$/;
  return allowedChars.test(value);
};

export const isValidEmail = (value) => {
  if (isBlank(value)) return true;

  const result = validateEmail(value);
  let invalid = !!result.code;

  // Additional validation for invalid characters
  if (!invalid) {
    invalid = INVALID_EMAIL_CHARS.some((v) => value.includes(v));
  }

  // Validate email format since op/validate library does not enforce domain extension
  if (!invalid) {
    const emailFormat = /^(.+)@(.+)\.(.+)$/;
    invalid = !emailFormat.test(value);
  }

  return !invalid;
};

export const useUpdateIncluded = (key, included, updateIncluded) => {
  useEffect(() => {
    updateIncluded(key, included);

    const disposer = reaction(
      () => included,
      () => updateIncluded(key, included)
    );

    return () => disposer();
  }, [key, included, updateIncluded]);
};

export const validateTextField = (value) => {
  if (isBlank(value)) return true;

  const allowedChars = /^[a-zA-ZÀ-ÖØ-öø-ÿ0-9ÄäÖöÅå'‘’\- ]+$/;
  return allowedChars.test(value);
};

export const resolveSubjectByTarget = (target, subjectFieldValue) => {
  let subject;
  if (target === BANKING || target === INSURANCE) {
    subject = subjectFieldValue;
  } else {
    subject = OTHER;
  }

  return subject;
};

export const resolveBrowserAndOS = () => {
  const browserAndOS = `${browserName} ${browserVersion}, ${osName} ${osVersion}`;
  return browserAndOS.substring(0, 40);
};

export const focusAtElement = (el) => {
  const element = document.querySelector(el);
  if (element) element.focus();
};

export const resolveCaseType = (target) => {
  return target === INSURANCE ? INSURANCE_FEEDBACK : BANK_FEEDBACK;
};
